/* WLFX — the WL global filter's client-side data plane (docs/project/wl-global-filter.md).
 *
 * Holds the two halves the filter matches against, so a keystroke costs ZERO network round trips:
 *
 *  - the STATIC per-pair index (wordlist-filter-index, GET + weak ETag so the browser keeps it
 *    across sessions): every virtual list's member ids in the list's own display order, plus one
 *    id → term-text map. Memberships are server VERDICTS — the client never re-derives a rule.
 *  - the PER-USER sets (wordlist-filter-user-sets): own lists, My Garden's sections (rows carry
 *    the same parked/removable/marks extras the band pages emit) and My phrases, loaded at page
 *    open and kept fresh here: a debounced refetch fires after any word-list-mutating request
 *    (this file watches ajax traffic), so the panel never has to remember to tell us.
 *
 * Data only — matching, painting and rendering stay in main.js's WordListsPanel, which owns the
 * one copy of the language filter rules (filterRegex). Every accessor returns null until its data
 * has arrived; callers treat null as "fall back to the server path", so a failed fetch degrades
 * to exactly the behaviour the panel had before this module existed.
 */
window.WLFX = (function () {
	"use strict";

	var payload = null;    // static index: {termLang, defLang, lists:{KEY:[ids]}, topics:{KEY:[ids]}, terms:{id:term}}
	var sets = null;       // per-user: {own:{listId:[rows]}, garden:{SEC:[rows]}, phrases:[rows]}
	var pairKey = "";      // "DE>EN" — guards late responses after a language switch
	var rowCache = {};     // listKey -> [{id,term}] built lazily from payload ids
	var setsLoadedAt = 0;
	var refreshTimer = null;

	// Requests that change what a user's lists contain. A match schedules ONE debounced user-sets
	// refetch — cheaper and far less fragile than hooking a dozen success handlers in main.js.
	var MUTATING_URL = /wordlist-(add-word|delete-word|delete-selected|delete-all|copy-selected-to|move-selected-to|create|delete\b|restore|rename)|term-capture|capture-phrase/;

	// The static index is the single largest thing the app downloads — 1.7 MB of JSON, ~620 KB on
	// the wire for DE→EN — and the panel's own load() fires it in the middle of the app-open burst,
	// where it competes for the browser's six connections with the fonts and icons the user can
	// actually see. Nobody types in the filter in the first second, so it waits for the browser to
	// go idle (timeout: it must not wait for a page that never does). Arriving late costs nothing:
	// every accessor returns null until the payload lands and callers read null as "ask the server",
	// which is exactly the behaviour the panel had before this module existed.
	function whenIdle(fn) {
		if (window.requestIdleCallback) {
			window.requestIdleCallback(fn, { timeout: 2000 });
		} else {
			setTimeout(fn, 400);
		}
	}

	function load(termLang, defLang) {
		if (!termLang || !defLang) { return; }
		var key = termLang + ">" + defLang;
		if (key !== pairKey) {
			pairKey = key;
			payload = null;
			sets = null;
			rowCache = {};
			whenIdle(function () {
				// A language switch while this was queued: the pair we were asked for is no longer
				// the pair on screen, and its own load() has already queued the right fetch.
				if (pairKey !== key) { return; }
				// cache:true on purpose — util.js's global ajaxSetup({cache:false}) would append a
				// timestamp and defeat the ETag'd browser cache this endpoint exists to use.
				$.ajax("wordlist-filter-index", {
					type: "GET",
					data: { termLang: termLang, defLang: defLang },
					dataType: "json",
					cache: true,
					success: function (data) {
						if (pairKey === key && data && data.terms) {
							payload = data;
							rowCache = {};
						}
					},
					error: function () { /* payload stays null → consumers fall back to the server */ }
				});
			});
		}
		refreshUserSets();
	}

	function refreshUserSets() {
		if (!pairKey) { return; }
		var key = pairKey;
		var langs = key.split(">");
		ajaxPost("wordlist-filter-user-sets", { termLang: langs[0], defLang: langs[1] },
			function (data) {
				if (pairKey !== key || !data) { return; }
				var own = {};
				$.each(data.ownLists || [], function (i, l) { own[l.id] = l.terms || []; });
				sets = { own: own, garden: data.garden || {}, phrases: data.phrases || [] };
				setsLoadedAt = Date.now();
			},
			null,
			function () { /* background nicety — stale sets beat an error box */ });
	}

	function scheduleRefresh() {
		clearTimeout(refreshTimer);
		refreshTimer = setTimeout(refreshUserSets, 1500);
	}

	// One listener covers every mutation path, present and future, that goes through these URLs.
	$(document).ajaxSuccess(function (e, xhr, opts) {
		if (opts && opts.url && MUTATING_URL.test(opts.url)) {
			scheduleRefresh();
		}
	});

	function rowsFromIds(ids) {
		var terms = payload.terms;
		var out = [];
		for (var i = 0; i < ids.length; i++) {
			var t = terms[ids[i]];
			if (t !== undefined) {
				out.push({ id: ids[i], term: t });
			}
		}
		return out;
	}

	function cachedRows(cacheKey, ids) {
		if (!rowCache[cacheKey]) {
			rowCache[cacheKey] = rowsFromIds(ids);
		}
		return rowCache[cacheKey];
	}

	return {
		load: load,

		/** True once the static index is in hand — the gate for every client-side filter path. */
		ready: function () { return !!payload; },

		/** True once the per-user sets are in hand. */
		userReady: function () { return !!sets; },

		/**
		 * A virtual list's rows in list order, or null: key = LEVEL_A..E, CONJUGATION,
		 * CONJUGATION_IRR (the onlyIrregular subset — pick by the toggle), VERBS.
		 */
		virtualRows: function (key) {
			if (!payload || !payload.lists[key]) { return null; }
			return cachedRows(key, payload.lists[key]);
		},

		/** One topic's rows in learner-rank order, or null. */
		topicRows: function (topicKey) {
			if (!payload || !payload.topics[topicKey]) { return null; }
			return cachedRows("TOPIC:" + topicKey, payload.topics[topicKey]);
		},

		/** Distinct rows across ALL topics (the Topics row's badge population), or null. */
		topicAllRows: function () {
			if (!payload) { return null; }
			if (!rowCache["TOPIC:*"]) {
				var seen = {};
				var ids = [];
				$.each(payload.topics, function (k, list) {
					for (var i = 0; i < list.length; i++) {
						if (!seen[list[i]]) { seen[list[i]] = true; ids.push(list[i]); }
					}
				});
				rowCache["TOPIC:*"] = rowsFromIds(ids);
			}
			return rowCache["TOPIC:*"];
		},

		/** An own list's rows in list order, or null when the sets (or the list) are unknown. */
		ownRows: function (listId) {
			return sets && sets.own[listId] ? sets.own[listId] : null;
		},

		/** Keep an own list fresh from a whole-list server response the panel already made. */
		setOwnRows: function (listId, rows) {
			if (sets) { sets.own[listId] = rows || []; }
		},

		/**
		 * Words just left an own list — drop them from its cached rows NOW.
		 *
		 * The debounced refetch above is 1.5s behind, and the panel re-renders the expanded list
		 * the instant the mutation returns. With a filter typed, that render answers from THESE
		 * rows (no round trip), so without this the word the user just moved or removed is drawn
		 * straight back and only vanishes when the list is closed and re-opened.
		 */
		dropOwnRows: function (listId, termIds) {
			if (!sets || !sets.own[listId] || !termIds || !termIds.length) { return; }
			var gone = {};
			for (var i = 0; i < termIds.length; i++) { gone[termIds[i]] = 1; }
			sets.own[listId] = $.grep(sets.own[listId], function (r) { return !gone[r.id]; });
		},

		/** One garden section's rows in section order (extras included), or null. */
		gardenRows: function (secKey) {
			return sets && sets.garden[secKey] ? sets.garden[secKey] : null;
		},

		/** Distinct garden rows across sections, STRUGGLING excluded (it is an overlay). */
		gardenAllRows: function () {
			if (!sets) { return null; }
			var seen = {};
			var out = [];
			$.each(sets.garden, function (sec, rows) {
				if (sec === "STRUGGLING") { return; }
				for (var i = 0; i < rows.length; i++) {
					if (!seen[rows[i].id]) { seen[rows[i].id] = true; out.push(rows[i]); }
				}
			});
			return out;
		},

		/** My phrases rows, newest first, or null. */
		phraseRows: function () {
			return sets ? sets.phrases : null;
		},

		/** Refetch the per-user sets if they are older than maxAgeMs (0 = always). */
		freshenUserSets: function (maxAgeMs) {
			if (!sets || Date.now() - setsLoadedAt > (maxAgeMs || 0)) {
				refreshUserSets();
			}
		}
	};
})();
